import { z } from 'zod';

/**
 * Shared client-side validation — mirrors the backend Zod schemas so the UI
 * and API never drift. Email/username are normalized before validation.
 */

const RESERVED = new Set([
  'admin', 'administrator', 'root', 'system', 'playerso', 'support', 'help',
  'mod', 'moderator', 'staff', 'official', 'api', 'auth', 'login', 'logout',
  'register', 'signin', 'signup', 'settings', 'account', 'me', 'self', 'null',
  'undefined', 'test', 'demo', 'guest', 'anonymous', 'superuser', 'sys',
  'security', 'billing', 'finance', 'info', 'contact', 'about', 'blog',
  'sell', 'buy', 'browse', 'search', 'forum', 'owner', 'operator', 'manager',
  'dev', 'developer', 'service', 'services',
]);

const username = z
  .string()
  .min(3, 'Username must be at least 3 characters')
  .max(32, 'Username must be at most 32 characters')
  .regex(/^[a-zA-Z0-9_]+$/, 'Username may only contain letters, numbers, and underscores')
  .refine((v) => !RESERVED.has(v.toLowerCase()), 'This username is reserved');

const email = z
  .string()
  .min(1, 'Email is required')
  .email('Enter a valid email address')
  .max(254, 'Email is too long')
  .transform((v) => v.trim().toLowerCase());

const displayName = z
  .string()
  .min(1, 'Display name is required')
  .max(64, 'Display name is too long')
  .transform((v) => v.trim().replace(/\s+/g, ' '));

const password = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/[a-z]/, 'Password must include a lowercase letter')
  .regex(/[A-Z]/, 'Password must include an uppercase letter')
  .regex(/[0-9]/, 'Password must include a number');

export const signInSchema = z.object({
  email,
  password: z.string().min(1, 'Password is required').max(128),
  rememberMe: z.boolean(),
});

export const signUpSchema = z
  .object({
    email,
    username,
    displayName,
    password,
    confirmPassword: z.string(),
    acceptTerms: z.boolean().refine((v) => v === true, 'You must accept the Terms of Service'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SignInValues = z.infer<typeof signInSchema>;
export type SignUpValues = z.infer<typeof signUpSchema>;